import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, Appointment } from "../lib/api";
import { useResponsive } from "../hooks/useResponsive";
import {
  EmptyState,
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
  AppointmentCard,
  TrainerBadge,
} from "../components/shared";

type CalendarViewType = "today" | "week" | "month";

interface CalendarToolbarProps {
  calendarView: CalendarViewType;
  onViewChange: (view: CalendarViewType) => void;
  currentDate: Date;
  onNavigate: (direction: number) => void;
  onToday: () => void;
  isMobile: boolean;
}

function CalendarToolbar({
  calendarView,
  onViewChange,
  currentDate,
  onNavigate,
  onToday,
  isMobile,
}: CalendarToolbarProps) {
  const formatDateTitle = () => {
    if (calendarView === "today") {
      return currentDate.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    } else if (calendarView === "week") {
      const weekStart = new Date(currentDate);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    } else {
      return currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }
  };

  return (
    <div className={`bg-white border-b border-slate-200 ${isMobile ? "p-3" : "px-6 py-4"}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <button
            onClick={onToday}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            Today
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onNavigate(-1)}
              className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <IconChevronLeft size={20} />
            </button>
            <button
              onClick={() => onNavigate(1)}
              className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              <IconChevronRight size={20} />
            </button>
          </div>
          <h2 className={`font-semibold text-slate-800 ${isMobile ? "text-base" : "text-lg"}`}>
            {formatDateTitle()}
          </h2>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-lg p-1">
            {(["today", "week", "month"] as CalendarViewType[]).map((view) => (
              <button
                key={view}
                onClick={() => onViewChange(view)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  calendarView === view
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                {view === "today" ? "Day" : view.charAt(0).toUpperCase() + view.slice(1)}
              </button>
            ))}
          </div>
          {!isMobile && (
            <Link
              to="/appointments/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors no-underline"
            >
              <IconPlus size={16} />
              New Appointment
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function getTrainerInitials(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.trim().split(" ").filter((p) => p.length > 0);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return (parts[0] ?? "").substring(0, 2).toUpperCase();
  const first = parts[0] ?? "";
  const last = parts[parts.length - 1] ?? "";
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase();
}

interface TodayViewProps {
  appointments: Appointment[];
  currentDate: Date;
  isMobile: boolean;
  allAppointments?: Appointment[];
}

function TodayView({ appointments, currentDate, isMobile, allAppointments }: TodayViewProps) {
  const todayStr = currentDate.toDateString();
  const todayAppointments = appointments
    .filter((a) => new Date(a.startTime).toDateString() === todayStr)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  // Calculate team stats from all appointments for today
  const allTodayAppointments = allAppointments
    ? allAppointments.filter((a) => new Date(a.startTime).toDateString() === todayStr)
    : todayAppointments;

  // Group by trainer (calendar)
  const trainerMap = new Map<
    string,
    { name: string; initials: string; color: string; count: number }
  >();
  for (const apt of allTodayAppointments) {
    const calendarId = apt.calendarId || "unknown";
    const existing = trainerMap.get(calendarId);
    if (existing) {
      existing.count++;
    } else {
      trainerMap.set(calendarId, {
        name: apt.calendarName || "Unknown",
        initials: getTrainerInitials(apt.calendarName),
        color: apt.calendarColor || "#6366F1",
        count: 1,
      });
    }
  }
  const trainersToday = Array.from(trainerMap.values());

  return (
    <div className={`flex-1 flex flex-col overflow-hidden ${isMobile ? "p-3" : "p-4"}`}>
      {/* Stats Bar */}
      <div className="flex gap-3 mb-4 flex-wrap items-stretch">
        {/* Total appointments */}
        <div className="bg-white rounded-lg py-3 px-4 border border-slate-200 flex items-center gap-3 min-w-[140px]">
          <div className="text-3xl font-bold text-slate-800">
            {todayAppointments.length}
          </div>
          <div className="text-xs text-slate-500 leading-tight">
            Appointments
            <br />
            Today
          </div>
        </div>

        {/* Team members working today */}
        {trainersToday.length > 0 && !isMobile && (
          <div className="bg-white rounded-lg py-3 px-4 border border-slate-200 flex items-center gap-3 flex-1 min-w-[200px]">
            <div className="text-xs text-slate-500 mr-2">Team:</div>
            <div className="flex gap-4 flex-wrap">
              {trainersToday.map((trainer, idx) => (
                <div
                  key={trainer.name}
                  className={`flex items-center gap-1.5 ${
                    idx < trainersToday.length - 1 ? "pr-4 border-r border-slate-200" : ""
                  }`}
                >
                  <TrainerBadge
                    initials={trainer.initials}
                    color={trainer.color}
                    name={trainer.name}
                  />
                  <span className="text-xs text-slate-800 font-medium">
                    {trainer.name.split(" ")[0]}
                  </span>
                  <span className="text-[11px] text-slate-500 font-semibold bg-slate-100 px-1.5 py-0.5 rounded-full">
                    {trainer.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Appointments List or Empty State */}
      {todayAppointments.length === 0 ? (
        <EmptyState
          title="No appointments today"
          description="Your schedule is clear. Book a new appointment or check another day."
          actionLabel="New Appointment"
          onAction={() => {
            window.location.href = "/appointments/new";
          }}
        />
      ) : (
        <div className="flex-1 overflow-auto">
          <div className="flex flex-col gap-3 max-w-2xl">
            {todayAppointments.map((apt) => (
              <AppointmentCard
                key={apt.id}
                appointment={apt}
                showTrainerBadge={true}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface WeekViewProps {
  appointments: Appointment[];
  currentDate: Date;
  isMobile: boolean;
  onNavigateToDay: (date: Date) => void;
}

function WeekView({ appointments, currentDate, isMobile, onNavigateToDay }: WeekViewProps) {
  const weekStart = new Date(currentDate);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());

  const days = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    return day;
  });

  const hours = Array.from({ length: 12 }, (_, i) => i + 7);

  const getAppointmentsForDayHour = (day: Date, hour: number) => {
    return appointments.filter((apt) => {
      const start = new Date(apt.startTime);
      return start.toDateString() === day.toDateString() && start.getHours() === hour;
    });
  };

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString();

  return (
    <div className="flex-1 overflow-auto">
      <div className={`min-w-[800px] ${isMobile ? "" : "px-6"}`}>
        <div className="grid grid-cols-8 border-b border-slate-200 bg-white sticky top-0">
          <div className="p-2" />
          {days.map((day) => (
            <button
              key={day.toISOString()}
              onClick={() => onNavigateToDay(day)}
              className={`p-2 text-center border-l border-slate-200 hover:bg-slate-50 ${
                isToday(day) ? "bg-blue-50" : ""
              }`}
            >
              <div className="text-xs text-slate-500">
                {day.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div
                className={`text-lg font-semibold ${isToday(day) ? "text-blue-600" : "text-slate-800"}`}
              >
                {day.getDate()}
              </div>
            </button>
          ))}
        </div>

        {hours.map((hour) => (
          <div key={hour} className="grid grid-cols-8 border-b border-slate-100">
            <div className="p-2 text-xs text-slate-500 text-right pr-3">
              {hour % 12 || 12}:00 {hour >= 12 ? "PM" : "AM"}
            </div>
            {days.map((day) => {
              const dayAppointments = getAppointmentsForDayHour(day, hour);
              return (
                <div
                  key={`${day.toISOString()}-${hour}`}
                  className="min-h-[60px] border-l border-slate-200 p-1"
                >
                  {dayAppointments.map((apt) => (
                    <Link
                      key={apt.id}
                      to={`/appointments/${apt.id}`}
                      className="block text-xs p-1 rounded mb-1 no-underline truncate bg-blue-100 text-blue-700 border-l-2 border-blue-500"
                    >
                      {apt.clientFirstName || "Client"} - {apt.serviceName || "Service"}
                    </Link>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

interface MonthViewProps {
  appointments: Appointment[];
  currentDate: Date;
  isMobile: boolean;
  onNavigateToDay: (date: Date) => void;
}

function MonthView({ appointments, currentDate, isMobile, onNavigateToDay }: MonthViewProps) {
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startOffset = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const weeks: (Date | null)[][] = [];
  let currentWeek: (Date | null)[] = [];

  for (let i = 0; i < startOffset; i++) {
    currentWeek.push(null);
  }

  for (let day = 1; day <= totalDays; day++) {
    currentWeek.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  while (currentWeek.length > 0 && currentWeek.length < 7) {
    currentWeek.push(null);
  }
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  const getAppointmentsForDay = (day: Date) => {
    return appointments.filter(
      (apt) => new Date(apt.startTime).toDateString() === day.toDateString()
    );
  };

  const isToday = (date: Date) => date.toDateString() === new Date().toDateString();

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className={`flex-1 overflow-auto ${isMobile ? "p-3" : "p-6"}`}>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200">
          {weekDays.map((day) => (
            <div
              key={day}
              className="p-2 text-center text-xs font-medium text-slate-500 bg-slate-50"
            >
              {day}
            </div>
          ))}
        </div>

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 border-b border-slate-200 last:border-b-0">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={`min-h-[100px] p-1 border-r border-slate-200 last:border-r-0 ${
                  day ? "cursor-pointer hover:bg-slate-50" : "bg-slate-50"
                }`}
                onClick={() => day && onNavigateToDay(day)}
              >
                {day && (
                  <>
                    <div
                      className={`text-sm font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                        isToday(day) ? "bg-blue-500 text-white" : "text-slate-700"
                      }`}
                    >
                      {day.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {getAppointmentsForDay(day)
                        .slice(0, 3)
                        .map((apt) => (
                          <div
                            key={apt.id}
                            className="text-xs p-0.5 rounded truncate bg-blue-100 text-blue-700"
                          >
                            {apt.clientFirstName || "Client"}
                          </div>
                        ))}
                      {getAppointmentsForDay(day).length > 3 && (
                        <div className="text-xs text-slate-500">
                          +{getAppointmentsForDay(day).length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CalendarPage() {
  const { isMobile } = useResponsive();
  const [calendarView, setCalendarView] = useState<CalendarViewType>("today");
  const [currentDate, setCurrentDate] = useState(new Date());

  const getDateRange = () => {
    if (calendarView === "today") {
      const start = new Date(currentDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(currentDate);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    } else if (calendarView === "week") {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay());
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + 7);
      return { start, end };
    } else {
      const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
      return { start, end };
    }
  };

  const { start, end } = getDateRange();

  const { data, isLoading } = useQuery({
    queryKey: ["calendar-appointments", start.toISOString(), end.toISOString()],
    queryFn: () =>
      api.appointments.list({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        limit: 100,
      }),
  });

  const navigateDate = (direction: number) => {
    const newDate = new Date(currentDate);
    if (calendarView === "today") {
      newDate.setDate(newDate.getDate() + direction);
    } else if (calendarView === "week") {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const handleNavigateToDay = (date: Date) => {
    setCurrentDate(date);
    setCalendarView("today");
  };

  const appointments = data?.data || [];

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <CalendarToolbar
        calendarView={calendarView}
        onViewChange={setCalendarView}
        currentDate={currentDate}
        onNavigate={navigateDate}
        onToday={() => setCurrentDate(new Date())}
        isMobile={isMobile}
      />

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-slate-500">Loading appointments...</div>
        </div>
      ) : calendarView === "week" ? (
        <WeekView
          appointments={appointments}
          currentDate={currentDate}
          isMobile={isMobile}
          onNavigateToDay={handleNavigateToDay}
        />
      ) : calendarView === "month" ? (
        <MonthView
          appointments={appointments}
          currentDate={currentDate}
          isMobile={isMobile}
          onNavigateToDay={handleNavigateToDay}
        />
      ) : (
        <TodayView
          appointments={appointments}
          currentDate={currentDate}
          isMobile={isMobile}
        />
      )}

      {isMobile && (
        <Link
          to="/appointments/new"
          className="fixed bottom-6 right-6 w-14 h-14 bg-blue-500 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-blue-600 transition-colors z-50"
        >
          <IconPlus size={24} />
        </Link>
      )}
    </div>
  );
}
